import React, { useState } from 'react';
import { X, Loader2, Wand2, Copy, FileText, Download, ExternalLink } from 'lucide-react';
import { generateTaskDescription } from '../lib/openai';
import { toast } from '../lib/toast';
import { jsPDF } from 'jspdf';
import { supabase } from '../lib/supabase';

interface SavedOutput {
  id: string;
  saved: boolean;
}

interface TeamStandupAgentProps {
  onClose: () => void;
}

export default function TeamStandupAgent({ onClose }: TeamStandupAgentProps) {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [summaryGenerated, setSummaryGenerated] = useState(false);
  const [savedOutput, setSavedOutput] = useState<SavedOutput | null>(null);
  const [savingOutput, setSavingOutput] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) {
      toast.error('Please enter team standup notes');
      return;
    }

    setLoading(true);
    try {
      const prompt = `Create a structured standup summary from the following raw notes:

${input}

Format your response as follows:

Structured Standup Summary – [Today's Date]
Team Members: [List all team members mentioned]

Yesterday's Highlights:
[Bullet points of what each team member accomplished]

Today's Focus:
[Bullet points of what each team member is working on today]

Blockers:
[List any blockers or challenges mentioned]

Team Mood:
[Brief assessment of team morale and energy levels]

Suggestions for Follow-Up:
[2-4 actionable suggestions for the team lead]

Progress Trends:
[Brief analysis of overall team progress and momentum]

Use plain text formatting with line breaks. Be concise but comprehensive, capturing all key information from the notes.`;

      const response = await generateTaskDescription(prompt, 'task');
      setResult(response);
      setSummaryGenerated(true);
      toast.success('Team standup summary generated successfully');

      // Prepare for saving but don't save automatically
      setSavedOutput({
        id: crypto.randomUUID(),
        saved: false
      });
    } catch (error) {
      console.error('Error generating standup summary:', error);
      toast.error('Failed to generate standup summary');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOutput = async () => {
    if (!result || !savedOutput || savedOutput.saved) return;
    
    setSavingOutput(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to save outputs');
        setSavingOutput(false);
        return;
      }
      
      // First try to find the agent by ID
      const { data: existingAgent, error: fetchError } = await supabase
        .from('ai_agents')
        .select('id')
        .eq('id', '00000000-0000-0000-0000-000000000008')
        .limit(1)
        .maybeSingle();
      
      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 is "not found" error
        console.error('Error fetching agent:', fetchError);
        toast.error('Failed to save output');
        setSavingOutput(false);
        return;
      }
      
      let agentId: string;
      
      if (!existingAgent) {
        // Try to find by name
        const { data: agentByName, error: nameError } = await supabase
          .from('ai_agents')
          .select('id')
          .eq('name', 'Team Standup Summarizer')
          .limit(1)
          .maybeSingle();
          
        if (nameError && nameError.code !== 'PGRST116') {
          console.error('Error finding agent by name:', nameError);
          toast.error('Failed to save output');
          setSavingOutput(false);
          return;
        }
        
        if (agentByName) {
          agentId = agentByName.id;
        } else {
          // Create new agent
          const { data: newAgent, error: createError } = await supabase
            .from('ai_agents')
            .insert({
              name: 'Team Standup Summarizer',
              user_id: user.id,
              goals: 'Transform raw standup notes into structured summaries',
              system_prompt: 'You are an expert standup facilitator. Transform raw standup notes into structured summaries with key highlights and action items.',
              is_custom: false,
              agent_type: 'text'
            })
            .select('id')
            .single();
            
          if (createError) {
            console.error('Error creating agent:', createError);
            toast.error('Failed to create agent');
            setSavingOutput(false);
            return;
          }
          
          agentId = newAgent.id;
        }
      } else {
        agentId = existingAgent.id;
      }
      
      // Save output to ai_agent_outputs table
      const { error: insertError } = await supabase.from('ai_agent_outputs').insert({
        user_id: user.id,
        agent_id: agentId,
        prompt: input,
        output_url: result,
        generation_type: 'text',
        llm_model: 'gpt-3.5-turbo',
        metadata: {},
        agent_type: 'text'
      });
      
      if (insertError) {
        console.error('Error saving output:', insertError);
        toast.error('Failed to save output');
        setSavingOutput(false);
        return;
      }
      
      toast.success('Output saved successfully');
      setSavedOutput({...savedOutput, saved: true});
    } catch (error) {
      console.error('Error saving agent output:', error);
      toast.error('Failed to save output');
    } finally {
      setSavingOutput(false);
    }
  };

  const handleCopy = async () => {
    try {
      // Create a temporary element to properly strip HTML
      const tempElement = document.createElement('div');
      tempElement.innerHTML = result || '';
      const textContent = tempElement.textContent || tempElement.innerText || '';
      
      await navigator.clipboard.writeText(textContent);
      toast.success('Copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const downloadPDF = () => {
    try {
      if (!result) return;
      
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.width;
      const margin = 20;
      
      // Add header
      pdf.setFillColor(79, 70, 229); // Indigo color
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      // Add title with white text
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      const title = 'Team Standup Summary';
      const titleWidth = pdf.getStringUnitWidth(title) * 22 / pdf.internal.scaleFactor;
      const titleX = (pageWidth - titleWidth) / 2;
      pdf.text(title, titleX, 25);
      
      // Reset text color for content
      pdf.setTextColor(0, 0, 0);
      
      // Parse HTML content
      const parser = new DOMParser();
      const doc = parser.parseFromString(result, 'text/html');
      
      // Start position after header
      let y = 50;
      
      // Add original input section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Original Standup Notes:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const wrappedInput = pdf.splitTextToSize(input, pageWidth - (margin * 2));
      pdf.text(wrappedInput, margin, y);
      y += wrappedInput.length * 7 + 15;
      
      // Process each line of the result
      const lines = doc.body.textContent?.split('\n') || [];
      
      // Add a table of contents
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255); // Light blue background
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Summary Contents:', margin, y);
      y += 10;
      
      // Extract section titles for table of contents
      const sectionTitles = lines
        .filter(line => line.endsWith(':') || line.includes('Summary') || line.includes('Members:'))
        .map(line => line.trim())
        .filter(Boolean);
      
      // Add section titles to table of contents
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      sectionTitles.forEach((title, index) => {
        pdf.text(`${index + 1}. ${title}`, margin, y);
        y += 7;
      });
      
      y += 10;
      
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Structured Summary:', margin, y);
      y += 10;
      
      let inSection = false;
      let sectionTitle = '';
      
      lines.forEach(line => {
        // Check if we need a new page
        if (y > pdf.internal.pageSize.height - 20) {
          pdf.addPage();
          y = 20;
        }
        
        const trimmedLine = line.trim();
        
        // Skip empty lines
        if (!trimmedLine) {
          y += 5;
          return;
        }
        
        // Check if this is a section header
        if (trimmedLine.endsWith(':') || 
            trimmedLine.startsWith('Structured Standup Summary') ||
            trimmedLine.startsWith('Team Members:')) {
          
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          
          // Add background for section headers
          pdf.setFillColor(240, 247, 255);
          pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
          
          pdf.text(trimmedLine, margin, y);
          y += 10;
          
          inSection = true;
          sectionTitle = trimmedLine;
        } else if (trimmedLine.startsWith('-') || trimmedLine.startsWith('•')) {
          // This is a bullet point
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'normal');
          
          const bulletText = trimmedLine.substring(1).trim();
          const wrappedBullet = pdf.splitTextToSize(bulletText, pageWidth - (margin * 2) - 10);
          
          pdf.text('•', margin, y);
          pdf.text(wrappedBullet, margin + 5, y);
          y += wrappedBullet.length * 7;
        } else {
          // Regular text
          pdf.setFontSize(11);
          pdf.setFont('helvetica', 'normal');
          
          const wrappedText = pdf.splitTextToSize(trimmedLine, pageWidth - (margin * 2));
          pdf.text(wrappedText, margin, y);
          y += wrappedText.length * 7;
        }
      });
      
      // Add footer to all pages
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(9);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Generated on ${new Date().toLocaleDateString()} | Page ${i} of ${pageCount}`, margin, pdf.internal.pageSize.height - 10);
      }
      
      pdf.save('team-standup-summary.pdf');
    } catch (error) {
      console.error('Error creating PDF:', error);
      toast.error('Failed to create PDF');
    }
  };

  const downloadExamplePDF = () => {
    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.width;
      const margin = 20;
      
      // Add header
      pdf.setFillColor(79, 70, 229); // Indigo color
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      // Add title with white text
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      const title = 'Team Standup Summary';
      const titleWidth = pdf.getStringUnitWidth(title) * 22 / pdf.internal.scaleFactor;
      const titleX = (pageWidth - titleWidth) / 2;
      pdf.text(title, titleX, 25);
      
      // Reset text color for content
      pdf.setTextColor(0, 0, 0);
      
      // Start position after header
      let y = 50;
      
      // Example input section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Original Standup Notes:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const exampleInput = 
        "Priya fixed the email scheduling bug and is writing test cases. Jay is working on exporting data as CSV. Tanvi drafted the UI for the new analytics page and needs feedback. Krunal joined late today, still catching up with backlog tasks. Manav is reviewing old tickets and helping Tanvi with the layout. Everyone's generally calm, Tanvi seemed a little stressed about the tight timeline.";
      
      const wrappedInput = pdf.splitTextToSize(exampleInput, pageWidth - (margin * 2));
      pdf.text(wrappedInput, margin, y);
      y += wrappedInput.length * 7 + 15;
      
      // Example output section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Structured Summary:', margin, y);
      y += 10;
      
      // Structured Standup Summary
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255);
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Structured Standup Summary – April 4, 2025', margin, y);
      y += 10;
      
      pdf.setFillColor(240, 247, 255);
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Team Members: Priya, Jay, Tanvi, Krunal, Manav', margin, y);
      y += 15;
      
      // Yesterday's Highlights
      pdf.setFillColor(240, 247, 255);
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Yesterday\'s Highlights:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const highlights = [
        'Priya resolved the email scheduling issue.',
        'Jay began working on CSV export functionality.',
        'Tanvi created a rough draft of the analytics page UI.',
        'Krunal resumed work on backlogged tasks after a delayed start.',
        'Manav reviewed older tickets and assisted Tanvi with UI layout.'
      ];
      
      highlights.forEach(highlight => {
        pdf.text('•', margin, y);
        const wrappedHighlight = pdf.splitTextToSize(highlight, pageWidth - (margin * 2) - 10);
        pdf.text(wrappedHighlight, margin + 5, y);
        y += wrappedHighlight.length * 7;
      });
      
      y += 10;
      
      // Today's Focus
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255);
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Today\'s Focus:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const focus = [
        'Priya will complete unit test cases related to the scheduling module.',
        'Jay continues building out data export features.',
        'Tanvi is waiting for feedback to proceed with the analytics UI.',
        'Krunal is catching up with sprint tasks from the backlog.',
        'Manav will help with layout polishing and handle UI feedback implementation.'
      ];
      
      focus.forEach(item => {
        pdf.text('•', margin, y);
        const wrappedItem = pdf.splitTextToSize(item, pageWidth - (margin * 2) - 10);
        pdf.text(wrappedItem, margin + 5, y);
        y += wrappedItem.length * 7;
      });
      
      y += 10;
      
      // Blockers
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255);
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Blockers:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const blockers = [
        'Tanvi may need faster turnaround on design feedback to stay on schedule.'
      ];
      
      blockers.forEach(blocker => {
        pdf.text('•', margin, y);
        const wrappedBlocker = pdf.splitTextToSize(blocker, pageWidth - (margin * 2) - 10);
        pdf.text(wrappedBlocker, margin + 5, y);
        y += wrappedBlocker.length * 7;
      });
      
      y += 10;
      
      // Check if we need a new page
      if (y > pdf.internal.pageSize.height - 60) {
        pdf.addPage();
        y = 20;
      }
      
      // Team Mood
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255);
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Team Mood:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const mood = [
        'Overall, the team appears calm and steady.',
        'Tanvi is slightly overwhelmed due to a tight timeline but is managing.'
      ];
      
      mood.forEach(item => {
        pdf.text('•', margin, y);
        const wrappedItem = pdf.splitTextToSize(item, pageWidth - (margin * 2) - 10);
        pdf.text(wrappedItem, margin + 5, y);
        y += wrappedItem.length * 7;
      });
      
      y += 10;
      
      // Suggestions for Follow-Up
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255);
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Suggestions for Follow-Up:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const suggestions = [
        'Prioritize giving Tanvi actionable UI feedback early today to avoid delays.',
        'Check if Krunal needs help clearing backlogged tasks or reassignments.',
        'Make sure Jay\'s CSV export logic is aligned with what stakeholders need.'
      ];
      
      suggestions.forEach(suggestion => {
        pdf.text('•', margin, y);
        const wrappedSuggestion = pdf.splitTextToSize(suggestion, pageWidth - (margin * 2) - 10);
        pdf.text(wrappedSuggestion, margin + 5, y);
        y += wrappedSuggestion.length * 7;
      });
      
      y += 10;
      
      // Progress Trends
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255);
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Progress Trends:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const trends = [
        'Most core modules are being finalized or moved into testing.',
        'Test coverage is improving with Priya\'s additions.',
        'UI/UX refinements are picking up steam with Manav and Tanvi collaborating.'
      ];
      
      trends.forEach(trend => {
        pdf.text('•', margin, y);
        const wrappedTrend = pdf.splitTextToSize(trend, pageWidth - (margin * 2) - 10);
        pdf.text(wrappedTrend, margin + 5, y);
        y += wrappedTrend.length * 7;
      });
      
      // Add footer with date
      pdf.setFontSize(9);
      pdf.setTextColor(128, 128, 128);
      pdf.text(`Generated by PM Pro AI`, margin, pdf.internal.pageSize.height - 10);
      
      pdf.save('team-standup-example.pdf');
    } catch (error) {
      console.error('Error creating example PDF:', error);
      toast.error('Failed to create example PDF');
    }
  };

  const toggleFullResponse = () => {
    setShowFullResponse(!showFullResponse);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Team Standup Summarizer
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="bg-indigo-50 dark:bg-indigo-900/50 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-indigo-800 dark:text-indigo-200 mb-2">Instructions</h3>
            <p className="text-sm text-indigo-700 dark:text-indigo-300 mb-4">
              Paste your raw standup notes below. The AI will transform them into a structured summary with key highlights, focus areas, blockers, and actionable suggestions.
            </p>
            <div className="flex space-x-2">
              <button
                onClick={downloadExamplePDF}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-300"
              >
                <Download className="h-4 w-4 mr-1.5" />
                Download Example PDF
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="standup-notes" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Standup Notes
              </label>
              <textarea
                id="standup-notes"
                rows={6}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="w-full h-24 rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Paste your raw standup notes here..."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin h-5 w-5 mr-2" />
                    Generating Summary...
                  </>
                ) : (
                  <>
                    <Wand2 className="h-5 w-5 mr-2" />
                    Generate Summary
                  </>
                )}
              </button>
            </div>
          </form>

          {result && (
            <div className="mt-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Standup Summary
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={handleCopy}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-300"
                  >
                    <Copy className="h-4 w-4 mr-1.5" />
                    Copy
                  </button>
                  <button
                    onClick={downloadPDF}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <FileText className="h-4 w-4 mr-1.5" />
                    Download PDF
                  </button>
                  <button
                    onClick={handleSaveOutput}
                    disabled={savingOutput || (savedOutput && savedOutput.saved)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {savingOutput ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4 mr-1.5" />
                        Saving...
                      </>
                    ) : savedOutput && savedOutput.saved ? (
                      'Saved'
                    ) : (
                      'Save Output'
                    )}
                  </button>
                </div>
              </div>
              {summaryGenerated && (
                <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg mb-4">
                  <p className="text-sm text-green-700 dark:text-green-300 font-medium">
                    Team Standup Summary Generated Successfully! You can now download it as PDF.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}